import { addDoc, collection, doc, setDoc, serverTimestamp, arrayUnion } from 'firebase/firestore';
import app, { db } from '../services/firebase';
import { getDeviceInfo } from './device';
import { getSourceInfo } from './source';
import { AnalyticsEventDoc, AnalyticsEventName, DeviceInfo, SourceInfo } from './types';

/* ------------------------------------------------------------------
   Privacy-safe, dependency-free client tracker.
   - visitorId : random id persisted in localStorage (no cookies / no PII)
   - sessionId : random id per browser session (sessionStorage)
   Captures only: page path, referrer/UTM, device/browser/os/screen.
   Never collects IP, geography or personal data.
------------------------------------------------------------------- */

const VISITOR_KEY = 'sw_visitor_id';
const SESSION_KEY = 'sw_analytics_session';
const SESSION_STARTED_KEY = 'sw_analytics_session_started';
const FIRST_VISIT_KEY = 'sw_first_visit';
const ADMIN_PREFIX = '/v3-x-command-root';

const HEARTBEAT_INTERVAL_MS = 30_000;
const PAGEVIEW_DEBOUNCE_MS = 350;

let visitorId: string | null = null;
let sessionId: string | null = null;
let deviceInfo: DeviceInfo | null = null;
let sourceInfo: SourceInfo | null = null;
let lastHeartbeatAt = 0;
let heartbeatTimer: number | null = null;
let pageViewTimer: number | null = null;
let lastPageView: { path: string; at: number } | null = null;

function randomId(): string {
  return (
    'v' +
    Math.random().toString(36).slice(2, 10) +
    Date.now().toString(36).slice(-6)
  );
}

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(scope: 'local' | 'session', key: string, value: string) {
  try {
    if (scope === 'local') localStorage.setItem(key, value);
    else sessionStorage.setItem(key, value);
  } catch {
    /* storage unavailable — tracker degrades to per-page identity */
  }
}

function isEnabled(): boolean {
  return Boolean(app && db);
}

function shouldTrackPage(path: string): boolean {
  return !path.startsWith(ADMIN_PREFIX);
}

export function getVisitorId(): string {
  if (!visitorId) {
    visitorId = readStorage(VISITOR_KEY) || randomId();
    writeStorage('local', VISITOR_KEY, visitorId);
  }
  return visitorId;
}

function readSessionStorage(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

export function getSessionId(): string {
  if (!sessionId) {
    // Canonical store is sessionStorage (one id per tab session).
    // Fall back to the legacy localStorage key written by older builds so
    // in-flight sessions are not split, then adopt it into sessionStorage.
    sessionId = readSessionStorage(SESSION_KEY) || readStorage(SESSION_KEY) || randomId();
    writeStorage('session', SESSION_KEY, sessionId);
  }
  return sessionId;
}

function buildDoc(event: AnalyticsEventName, page: string, metadata?: Record<string, unknown>): Partial<AnalyticsEventDoc> {
  const now = new Date();
  const date = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');

  if (!deviceInfo) deviceInfo = getDeviceInfo();
  if (!sourceInfo) sourceInfo = getSourceInfo();

  return {
    event,
    timestamp: serverTimestamp(),
    date,
    page,
    sessionId: getSessionId(),
    visitorId: getVisitorId(),
    referrer: sourceInfo.referrer,
    source: sourceInfo.source,
    medium: sourceInfo.medium,
    campaign: sourceInfo.campaign,
    device: deviceInfo.device,
    browser: deviceInfo.browser,
    os: deviceInfo.os,
    screen: deviceInfo.screen,
    metadata: metadata && Object.keys(metadata).length > 0 ? metadata : undefined,
  };
}

export async function trackEvent(
  name: AnalyticsEventName,
  page?: string,
  metadata?: Record<string, unknown>
): Promise<void> {
  if (!isEnabled()) return;
  const resolvedPage = page ?? (typeof window !== 'undefined' ? window.location.pathname : '/');
  if (!shouldTrackPage(resolvedPage)) return;
  try {
    await addDoc(collection(db, 'analytics_events'), buildDoc(name, resolvedPage, metadata));
  } catch (e) {
    // Tracking must never break the user journey.
    if (import.meta.env.DEV) console.error('Analytics event failed:', e);
  }
}

export function trackPageView(path: string): void {
  if (!isEnabled() || !shouldTrackPage(path)) return;

  // Debounce rapid route changes (StrictMode double-effects, redirect chains).
  const now = Date.now();
  if (lastPageView && lastPageView.path === path && now - lastPageView.at < PAGEVIEW_DEBOUNCE_MS) {
    return;
  }
  lastPageView = { path, at: now };

  if (pageViewTimer) window.clearTimeout(pageViewTimer);
  pageViewTimer = window.setTimeout(() => {
    void trackEvent('page_view', path);
  }, 120);
}

function pushHeartbeat(page: string) {
  if (!isEnabled()) return;
  const now = Date.now();
  if (now - lastHeartbeatAt < HEARTBEAT_INTERVAL_MS) return;
  lastHeartbeatAt = now;

  if (!deviceInfo) deviceInfo = getDeviceInfo();

  const sessionDoc = doc(db, 'visitorSessions', getSessionId());
  void setDoc(
    sessionDoc,
    {
      visitorId: getVisitorId(),
      device: deviceInfo.device,
      browser: deviceInfo.browser,
      os: deviceInfo.os,
      screen: deviceInfo.screen,
      pages: arrayUnion(page),
      lastActivity: serverTimestamp(),
      startedAt: serverTimestamp(),
      status: 'active',
    },
    { merge: true }
  ).catch(() => {
    /* presence heartbeat is best-effort */
  });
}

function stopHeartbeat() {
  if (heartbeatTimer !== null) {
    window.clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
}

/** Called once per browser session. Sends session_start + first_visit and starts the heartbeat. */
export function initAnalyticsSession() {
  if (!isEnabled()) return;

  if (heartbeatTimer === null) {
    heartbeatTimer = window.setInterval(() => {
      if (typeof window !== 'undefined') pushHeartbeat(window.location.pathname);
    }, HEARTBEAT_INTERVAL_MS);
    pushHeartbeat(typeof window !== 'undefined' ? window.location.pathname : '/');
  }

  const started = readStorage(SESSION_STARTED_KEY);
  if (!started) {
    writeStorage('session', SESSION_STARTED_KEY, '1');
    void trackEvent('session_start', typeof window !== 'undefined' ? window.location.pathname : '/');
  }

  if (!readStorage(FIRST_VISIT_KEY)) {
    writeStorage('local', FIRST_VISIT_KEY, '1');
    void trackEvent('first_visit', typeof window !== 'undefined' ? window.location.pathname : '/');
  }
}

export function closeAnalytics(): void {
  stopHeartbeat();
  if (pageViewTimer !== null) window.clearTimeout(pageViewTimer);
  pageViewTimer = null;
}

/** QR menu scan helper for future QR surfaces. Emits nothing until called. */
export function trackQrMenuView(menuId: string, menuName?: string): Promise<void> {
  return trackEvent('qr_menu_view', `/qr-menu/${menuId}`, {
    menuId,
    ...(menuName ? { name: menuName } : {}),
  });
}

/** Attribution helpers for other collectors (e.g. the contact form). */
export function getAttribution() {
  if (!deviceInfo) deviceInfo = getDeviceInfo();
  if (!sourceInfo) sourceInfo = getSourceInfo();
  return {
    source: sourceInfo.source,
    medium: sourceInfo.medium,
    campaign: sourceInfo.campaign,
    referrer: sourceInfo.referrer,
    visitorId: getVisitorId(),
    sessionId: getSessionId(),
  };
}

export const ADMIN_ROUTE_PREFIX = ADMIN_PREFIX;